import {
  createContext,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  use,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import styles from './ConversationMessageComposer.module.css'
import { IconButton } from '@/components/IconButton'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { LayoutGroup, LazyMotion, m } from 'motion/react'
import { useCssVariable } from '@/features/shell/theming/use-css-variable'
import { shuffleTransition } from '@/consts/springs'
import { mergeProps, useFocus, useKeyboard } from 'react-aria'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import type { Observable } from 'rxjs'
import { useObservable } from '@/hooks/rx-hooks'

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

export interface ConversationMessageComposerProps {
  /**
   * The current value of the message composer's text input.
   */
  value?: string

  /**
   * Value change handler.
   */
  onChange?: (value: string) => unknown

  /**
   * Callback when the user wants to send the message.
   */
  onSend?: () => unknown

  /**
   * Ref for the root element.
   */
  ref?: Ref<HTMLDivElement>

  /**
   * Whether the composer is disabled.
   */
  isDisabled?: boolean

  /**
   * List of suggestions to show, if any.
   */
  suggestions?: readonly string[]

  /**
   * Callback when the user selects a suggestion.
   *
   * @param suggestion
   */
  onSelectSuggestion?: (suggestion: string) => unknown

  /**
   * After how many characters to stack the message box on top of the actions.
   *
   * @default 20
   */
  stackAfter?: number

  /**
   * Callback when the focus state changes.
   *
   * @param isFocused
   */
  onFocusChange?: (isFocused: boolean) => unknown

  /**
   * Blurs the composer text field when a value is emitted.
   */
  blur$?: Observable<unknown>

  /**
   * Whether sending on enter is disabled.
   */
  disableSendOnEnter?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string

  /**
   * Test ID attribute.
   */
  textareaTestId?: string

  /**
   * Test ID attribute.
   */
  sendButtonTestId?: string

  /**
   * Additional action elements to render in the leading (left) area.
   */
  leadingActions?: ReactNode
}

/**
 * Props context for {@link ConversationMessageComposer}.
 */
export const ConversationMessageComposerContext = createContext<
  Partial<ConversationMessageComposerProps>
>({})

/**
 * A component for composing messages in a conversation.
 */
export function ConversationMessageComposer(
  props: ConversationMessageComposerProps,
) {
  const {
    value,
    onChange,
    ref,
    onSend,
    onSelectSuggestion,
    isDisabled,
    onFocusChange,
    blur$,
    suggestions,
    disableSendOnEnter,
    stackAfter = 20,
    testId,
    textareaTestId,
    sendButtonTestId,
    leadingActions,
  } = mergeProps(use(ConversationMessageComposerContext), props)

  const borderRadius = useCssVariable('--border-radius-sm')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  useObservable(blur$, () => textareaRef.current?.blur())

  const isSpacious = useIsSpacious()
  const isStacked = useMemo(
    () => Boolean(value && (value.length > stackAfter || value.includes('\n'))),
    [value, stackAfter],
  )

  const shouldSendOnEnter = isSpacious && !disableSendOnEnter
  const placeholder = shouldSendOnEnter
    ? 'Shift + Enter to add a new line'
    : 'Message'

  const [isFocused, setIsFocused] = useState(false)
  const { focusProps } = useFocus({
    isDisabled: isDisabled,
    onFocusChange: useCallback(
      (isFocused: boolean) => {
        setIsFocused(isFocused)
        onFocusChange?.(isFocused)
      },
      [onFocusChange],
    ),
  })

  const { keyboardProps } = useKeyboard({
    onKeyDown: useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          // Always allow sending on enter with the meta or control key held.
          if (shouldSendOnEnter || e.metaKey || e.ctrlKey) {
            e.preventDefault()
            onSend?.()
          }
        }
      },
      [shouldSendOnEnter, onSend],
    ),
  })

  return (
    <LazyMotion features={loadMotionFeatures}>
      <m.div
        data-private
        data-test={testId}
        layout
        transition={shuffleTransition}
        ref={ref}
        style={{
          borderRadius,
        }}
        className={cx(
          styles.root,
          isStacked ? styles.stacked : styles.inline,
          isDisabled && styles.disabled,
          isFocused && styles.focus,
        )}
      >
        <LayoutGroup>
          <m.div
            layout
            transition={shuffleTransition}
            className={styles.secondary}
            style={{
              gridArea: isStacked ? '2 / 1' : '1 / 1',
            }}
          >
            {leadingActions && (
              <m.div layout="position" transition={shuffleTransition}>
                {leadingActions}
              </m.div>
            )}
            {onSelectSuggestion && suggestions && suggestions.length > 0 && (
              <m.div layout="position" transition={shuffleTransition}>
                <Dropdown
                  title="Suggested topics for assistance"
                  placement="top start"
                  offset={8}
                  trigger={
                    <IconButton
                      sm
                      isDisabled={isDisabled}
                      label="Show suggested topics"
                      name="sparkles-filled"
                    />
                  }
                >
                  {suggestions.map((suggestion) => (
                    <DropdownItem
                      key={suggestion}
                      textValue={suggestion}
                      icon="sparkles-filled"
                      onAction={() => onSelectSuggestion(suggestion)}
                    >
                      {suggestion}
                    </DropdownItem>
                  ))}
                </Dropdown>
              </m.div>
            )}
          </m.div>

          <m.div
            layout
            transition={shuffleTransition}
            style={{
              gridArea: isStacked ? '1 / 1 / 2 / 4' : '1 / 2',
            }}
          >
            <m.div
              layout="position"
              transition={shuffleTransition}
              className={styles.textareaContainer}
              data-replicated-value={value || placeholder}
            >
              <textarea
                ref={textareaRef}
                {...mergeProps(focusProps, keyboardProps)}
                // @ts-ignore: this exists
                enterKeyHint={shouldSendOnEnter ? 'send' : 'enter'}
                value={value}
                disabled={isDisabled}
                className={styles.textarea}
                onChange={
                  onChange ? (e) => onChange?.(e.target.value) : undefined
                }
                rows={1}
                placeholder={placeholder}
                aria-label="Message"
                data-test={textareaTestId}
              />
            </m.div>
          </m.div>

          <m.div
            layout
            transition={shuffleTransition}
            className={styles.primary}
            style={{
              gridArea: isStacked ? '2 / 3' : '1 / 3',
            }}
          >
            <m.div layout="position" transition={shuffleTransition}>
              <IconButton
                isDisabled={!value || isDisabled}
                sm
                onPress={onSend}
                variant="primary"
                label="Send message"
                name="send"
                testId={sendButtonTestId}
              />
            </m.div>
          </m.div>
        </LayoutGroup>
      </m.div>
    </LazyMotion>
  )
}
